// pages/Summary.js
import React, { useState, useEffect } from "react";
import api from "../api";
import RadarChart from "../components/RadarChart";
import TimeSeriesChart from "../components/TimeSeriesChart";
import MedicalRecordsChiefComplaint from "../components/MedicalRecordsChiefComplaint";
import MedicalRecordScrollTable from "../components/MedicalRecordScrollTable";
import QuestionDetailCard from "../components/QuestionDetailCard";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

// === 折りたたみパネル ===
function AccordionPanel({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      style={{
        width: "100%",
        border: "1px solid #ccc",
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => setOpen(!open)}
        style={{
          cursor: "pointer",
          background: "#f2f2f2",
          padding: "0.75rem 1rem",
          fontWeight: "bold",
          userSelect: "none",
        }}
      >
        {open ? "▼" : "▶"} {title}
      </div>
      {open && (
        <div style={{ padding: "1rem", borderTop: "1px solid #ccc" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// === パネル下部リンク ===
function PanelLink({ children, to, hidden, state }) {
  const navigate = useNavigate();
  if (hidden) return null;
  return (
    <p
      onClick={() => navigate(to, { state })}
      style={{
        color: "#0f62fe", // IBM Blue
        cursor: "pointer",
        marginTop: "1rem",
        textDecoration: "underline",
        fontWeight: 600,
        display: "inline-block",
      }}
    >
      {children}
    </p>
  );
}

export default function Summary() {
  const { role } = useAuth();
  const isPatient = role === "patient";

  const [consultationData, setConsultationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth() || {};
  const storedPatient = sessionStorage.getItem("patient");
  const patient = storedPatient ? JSON.parse(storedPatient) : null;
  const patientId = patient?.research_id || user?.id || "P010";

  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        let endpoint = "";

        if (role === "patient") {
          endpoint = `/v1/patient/patient_dashboard/${patientId}`;
        } else if (role === "attending_physician" || role === "expert_physician") {
          endpoint = `/v1/doctor/doctor_dashboard/${patientId}`; 
        } else {
          console.warn("未対応のrole:", role);
          return;
        }

        const res = await api.get(endpoint);
        setDashboardData(res.data);
      } catch (err) {
        console.error("dashboard取得エラー:", err);
      }
    };

    fetchDashboard();
  }, [patientId, role]);

  useEffect(() => {
    const aiDates = dashboardData?.ai_answer_date ?? [];
    if (aiDates.length) {
      sessionStorage.setItem("aiDates", JSON.stringify(aiDates));
      sessionStorage.setItem("lastPatientId", patientId);
    }
  }, [dashboardData, patientId]);

  useEffect(() => {
    const fetchConsultation = async () => {
      try {
        const res = await api.get(`/v1/doctor/patient_consultation/${patientId}`);
        setConsultationData(res.data);
      } catch (err) {
        console.error("診察情報取得エラー:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchConsultation();
  }, [patientId]);

  // === データの安全展開 ===
  const {
    you_made_happy,
    concerning_symptoms,
    symptoms_duration,
    concerns_or_questions,
    past_consultation = [],
  } = consultationData ?? {};

  return (
    <div
      style={{
        padding: "0.5rem",
        maxWidth: "1200px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
      }}
    >
      {/* レーダーチャート */}
      <RadarChart data={dashboardData} />

      {/* === 主訴＋診察履歴 === */}
      {!isPatient && (
        <AccordionPanel title="主訴・診察履歴">
          {loading && <p>読み込み中...</p>}
          {error && <p>データの取得に失敗しました。</p>}

          {!loading && !error && consultationData && (
            <>
              {/* 主訴 */}
              <div style={{ marginBottom: "1rem" }}>
                <h4 style={{ margin: "0 0 0.5rem" }}>主訴</h4>
                <MedicalRecordsChiefComplaint
                  patientName={patientId}
                  complaint={{
                    you_made_happy,
                    concerning_symptoms,
                    symptoms_duration,
                    concerns_or_questions,
                  }}
                />
              </div>

              {/* 診察履歴 */}
              <div>
                <h4 style={{ margin: "1rem 0 0.5rem" }}>診察履歴</h4>
                <MedicalRecordScrollTable records={past_consultation} />
              </div>

              {/* 詳細リンク */}
              <PanelLink to="/consultation-records">詳細</PanelLink>
            </>
          )}
        </AccordionPanel>
      )}

      {/* === AI問診：時系列グラフ === */}
      <AccordionPanel title="AI問診結果：時系列グラフ">
        <TimeSeriesChart data={dashboardData} />
        <PanelLink to="/ai-interview-scores" hidden={isPatient}>
          詳細
        </PanelLink>
      </AccordionPanel>

      {/* === AI問診：質問 === */}
      {!isPatient && (
        <AccordionPanel title="AI問診結果：質問">
          <QuestionDetailCard />
          <PanelLink
            to="/ai-interview-questions"
            state={{ aiDates: dashboardData?.ai_answer_date ?? [] }}
          >
            詳細
          </PanelLink>
        </AccordionPanel>
      )}
    </div>
  );
}
